/**
 * The calendar contract - personal meetings and reminders.
 *
 * Deliberately NOT a task: no assignment, no completion policy. An event
 * lives on its owner's calendar; inviting someone puts it on theirs too,
 * read-only unless they own it.
 *
 * A meeting carries a STATUS (tentative until confirmed, cancellable,
 * markable as rescheduled) and may REPEAT daily / weekly / monthly. A
 * repeating meeting is materialised as REAL rows at creation time (daily:
 * 30 occurrences, weekly: 12, monthly: 12) sharing one seriesId - so each
 * occurrence can be edited, cancelled or deleted on its own, and reminders
 * and invites work per-occurrence with no special cases.
 */
export const MeetingStatus = {
    TENTATIVE: 'TENTATIVE',
    CONFIRMED: 'CONFIRMED',
    CANCELLED: 'CANCELLED',
    RESCHEDULED: 'RESCHEDULED',
};
export const MEETING_STATUS_LABEL = {
    TENTATIVE: 'Tentative',
    CONFIRMED: 'Confirmed',
    CANCELLED: 'Cancelled',
    RESCHEDULED: 'Rescheduled',
};
/**
 * What kind of commitment this row is. A MEETING is the internal thing with
 * invitees and RSVPs; an ENGAGEMENT is public-facing - a rally, a town hall,
 * a press event - with a prep checklist and no invitations at all.
 *
 * Both live in the same table so that one clash check covers both: a rally
 * that collides with a meeting is exactly what the tracker must catch.
 */
export const CalendarEventKind = {
    MEETING: 'MEETING',
    ENGAGEMENT: 'ENGAGEMENT',
};
export const EngagementType = {
    RALLY: 'RALLY',
    TOWN_HALL: 'TOWN_HALL',
    PRESS_EVENT: 'PRESS_EVENT',
    OTHER: 'OTHER',
};
export const ENGAGEMENT_TYPE_LABEL = {
    RALLY: 'Rally',
    TOWN_HALL: 'Town hall',
    PRESS_EVENT: 'Press event',
    OTHER: 'Other engagement',
};
/**
 * The prep checklist that comes with each engagement type. Seeded at
 * creation and then editable per event - a starting point, not a rule.
 * Kept in shared so the API seeds exactly what the UI advertises.
 */
export const ENGAGEMENT_CHECKLIST_TEMPLATE = {
    RALLY: [
        'Venue booked and confirmed',
        'Police permission / permit obtained',
        'Stage, sound and lighting arranged',
        'Speakers confirmed and briefed',
        'Transport and parking plan',
        'Volunteer roster assigned',
        'Publicity: posters, social, WhatsApp groups',
        'Press invited',
        'Drinking water, medical and safety cover',
    ],
    TOWN_HALL: [
        'Venue booked and confirmed',
        'Agenda and running order agreed',
        'Panel / speakers confirmed',
        'Invitations sent to residents',
        'Questions collected in advance',
        'Seating, mic and recording arranged',
        'Note-taker assigned for follow-ups',
    ],
    PRESS_EVENT: [
        'Date and time cleared with the desk',
        'Press release drafted and approved',
        'Media list and invitations sent',
        'Spokesperson briefed with talking points',
        'Q&A prepared for likely questions',
        'Photographer / videographer arranged',
        'Backdrop and branding ready',
    ],
    OTHER: [
        'Venue confirmed',
        'Approvals obtained',
        'People assigned',
        'Publicity plan',
    ],
};
export const MeetingRepeat = {
    NONE: 'NONE',
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
};
export const MEETING_REPEAT_LABEL = {
    NONE: 'Does not repeat',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
};
/**
 * An invitee's answer. PENDING is "asked, has not said anything yet" -
 * distinct from TENTATIVE, which is the deliberate "not sure".
 */
export const MeetingResponse = {
    PENDING: 'PENDING',
    ACCEPTED: 'ACCEPTED',
    DECLINED: 'DECLINED',
    TENTATIVE: 'TENTATIVE',
};
export const MEETING_RESPONSE_LABEL = {
    PENDING: 'No answer yet',
    ACCEPTED: 'Accepted',
    DECLINED: 'Declined',
    TENTATIVE: 'Not sure',
};
/** How a past meeting was matched to the one being briefed. */
export const BriefingMatch = {
    /** An earlier occurrence of the same repeating series - the strongest. */
    SERIES: 'SERIES',
    /** A past meeting with the same title. */
    TITLE: 'TITLE',
    /** A past meeting sharing at least one of the same people. */
    PEOPLE: 'PEOPLE',
};
export const CALENDAR_EVENT_TITLE_MAX = 200;
export const CALENDAR_EVENT_DESCRIPTION_MAX = 4000;
export const CALENDAR_EVENT_LOCATION_MAX = 200;
/** The notepad is pasted into, not typed - a transcript must fit. */
export const MEETING_NOTES_MAX = 20_000;
/** Biggest file the meeting form will offer to upload. */
export const EVENT_FILE_NAME_MAX = 255;
export const ENGAGEMENT_TYPE_NAME_MAX = 60;
export const ENGAGEMENT_CHECKLIST_ITEM_MAX = 200;
export const ENGAGEMENT_CHECKLIST_ITEMS_MAX = 30;
/**
 * The human label for an engagement type code.
 *
 * Pass the organisation's loaded types when you have them - that is the
 * only source that knows a custom type's name. Without them it falls back
 * to the four built-in labels, and failing that prettifies the code, so a
 * custom type reads as "Door knock" rather than "DOOR_KNOCK" even in
 * places that cannot reach the database (a drafted email, say).
 */
export function engagementTypeLabel(code, types) {
    if (!code)
        return 'Other engagement';
    const owned = types?.find((t) => t.code === code);
    if (owned)
        return owned.name;
    const builtin = ENGAGEMENT_TYPE_LABEL[code];
    if (builtin)
        return builtin;
    const words = code.toLowerCase().replace(/_+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
//# sourceMappingURL=calendar.js.map